import React, { useState, useEffect, useRef } from 'react';
import {
  PROFESSIONAL_TYPE_LABELS,
  PROFESSIONAL_CATEGORIES,
  type ProfessionalCategoryId,
} from '../types/professionalRates';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import BelagomProfilePhoto from '../components/BelagomProfilePhoto';
import api from '../services/api';

// Fix leaflet default marker icon paths broken by bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Province centroids — fallback only
const PROVINCE_COORDS: Record<string, [number, number]> = {
  'Noord-Holland': [52.52, 4.88],
  'Zuid-Holland': [52.02, 4.49],
  'Utrecht': [52.09, 5.12],
  'Gelderland': [52.05, 5.86],
  'Noord-Brabant': [51.58, 5.23],
  'Limburg': [51.22, 5.93],
  'Zeeland': [51.47, 3.83],
  'Friesland': [53.11, 5.80],
  'Groningen': [53.22, 6.57],
  'Drenthe': [52.92, 6.62],
  'Overijssel': [52.45, 6.55],
  'Flevoland': [52.53, 5.60],
};

// Dutch city → [lat, lng] lookup (woonadres is leidend)
const CITY_COORDS: Record<string, [number, number]> = {
  'amsterdam': [52.3676, 4.9041],
  'rotterdam': [51.9225, 4.4792],
  'den haag': [52.0705, 4.3007],
  "'s-gravenhage": [52.0705, 4.3007],
  'utrecht': [52.0907, 5.1214],
  'eindhoven': [51.4416, 5.4697],
  'groningen': [53.2194, 6.5665],
  'tilburg': [51.5555, 5.0913],
  'almere': [52.3508, 5.2647],
  'breda': [51.5719, 4.7683],
  'nijmegen': [51.8426, 5.8546],
  'apeldoorn': [52.2112, 5.9699],
  'haarlem': [52.3874, 4.6462],
  'arnhem': [51.9851, 5.8987],
  'zaandam': [52.4385, 4.8240],
  'amersfoort': [52.1561, 5.3878],
  'den bosch': [51.6978, 5.3037],
  "'s-hertogenbosch": [51.6978, 5.3037],
  's hertogenbosch': [51.6978, 5.3037],
  'maastricht': [50.8514, 5.6910],
  'leiden': [52.1601, 4.4970],
  'dordrecht': [51.8133, 4.6901],
  'zwolle': [52.5168, 6.0830],
  'zoetermeer': [52.0576, 4.4940],
  'deventer': [52.2512, 6.1583],
  'delft': [52.0116, 4.3571],
  'enschede': [52.2215, 6.8937],
  'hilversum': [52.2292, 5.1736],
  'alkmaar': [52.6324, 4.7534],
  'venlo': [51.3704, 6.1724],
  'leeuwarden': [53.2012, 5.7999],
  'helmond': [51.4812, 5.6614],
  'lelystad': [52.5185, 5.4714],
  'middelburg': [51.4988, 3.6136],
  'oss': [51.7655, 5.5186],
  'roosendaal': [51.5311, 4.4639],
  'purmerend': [52.5027, 4.9604],
  'emmen': [52.7792, 6.8936],
  'hengelo': [52.2659, 6.7930],
  'spijkenisse': [51.8418, 4.3290],
  'vlaardingen': [51.9124, 4.3424],
  'schiedam': [51.9186, 4.3985],
  'capelle aan den ijssel': [51.9333, 4.5833],
  'nieuwegein': [52.0316, 5.0872],
  'hoorn': [52.6432, 5.0602],
  'alphen aan den rijn': [52.1277, 4.6538],
  'gouda': [52.0116, 4.7072],
  'westland': [52.0167, 4.1833],
  'zaanstad': [52.4385, 4.8240],
  'velsen': [52.4593, 4.6460],
  'haarlemmermeer': [52.3017, 4.6889],
  'ede': [52.0436, 5.6636],
  'doetinchem': [51.9671, 6.2966],
  'hardenberg': [52.5765, 6.6189],
  'midden-delfland': [51.9917, 4.2750],
  'westvoorne': [51.8820, 4.0720],
  'waalwijk': [51.6836, 5.0654],
  'weert': [51.2515, 5.7082],
  'venray': [51.5271, 5.9741],
  'goes': [51.5047, 3.8897],
  'bergen op zoom': [51.4947, 4.2886],
  'terneuzen': [51.3352, 3.8290],
  'vlissingen': [51.4420, 3.5697],
  'sittard': [51.0016, 5.8726],
  'geleen': [50.9734, 5.8350],
  'heerlen': [50.8878, 5.9797],
  'roermond': [51.1933, 5.9875],
  'kerkrade': [50.8650, 6.0672],
  'vught': [51.6536, 5.2928],
  'boxtel': [51.5969, 5.3363],
  'veldhoven': [51.4167, 5.4000],
  'geldrop': [51.4176, 5.5551],
  'nuenen': [51.4667, 5.5500],
  'best': [51.5112, 5.3918],
  'oisterwijk': [51.5783, 5.1983],
  'loon op zand': [51.6250, 5.0753],
  'dongen': [51.6237, 4.9375],
  'meierijstad': [51.6167, 5.4500],
  'landerd': [51.7333, 5.6500],
  'uden': [51.6614, 5.6189],
  'veghel': [51.6146, 5.5473],
  'bernheze': [51.6833, 5.5500],
  'cuijk': [51.7300, 5.8783],
  'grave': [51.7578, 5.7408],
  'hattem': [52.4742, 6.0619],
  'harderwijk': [52.3433, 5.6233],
  'elburg': [52.4408, 5.8347],
  'nunspeet': [52.3839, 5.7844],
  'ermelo': [52.3017, 5.6217],
  'putten': [52.2581, 5.6019],
  'nijkerk': [52.2175, 5.4864],
  'barneveld': [52.1411, 5.5844],
  'wageningen': [51.9692, 5.6658],
  'tiel': [51.8889, 5.4283],
  'zaltbommel': [51.8167, 5.2500],
  'gorinchem': [51.8329, 4.9753],
  'vianen': [51.9833, 5.0833],
  'nieuwkoop': [52.1500, 4.7833],
  'katwijk': [52.2015, 4.4065],
  'lisse': [52.2556, 4.5578],
  'sassenheim': [52.2236, 4.5194],
  'noordwijk': [52.2389, 4.4436],
  'wassenaar': [52.1444, 4.4014],
  'voorburg': [52.0667, 4.3667],
  'leidschendam': [52.0833, 4.3833],
  'rijswijk': [52.0403, 4.3231],
  'pijnacker': [52.0228, 4.4281],
  'lansingerland': [52.0000, 4.5000],
  'barendrecht': [51.8594, 4.5344],
  'ridderkerk': [51.8661, 4.6033],
  'hendrik-ido-ambacht': [51.8464, 4.6219],
  'papendrecht': [51.8333, 4.6833],
  'sliedrecht': [51.8294, 4.7714],
  'alblasserdam': [51.8669, 4.6614],
  'krimpen aan den ijssel': [51.9128, 4.5969],
  'ijsselstein': [52.0167, 5.0333],
  'woerden': [52.0861, 4.8847],
  'oudewater': [52.0222, 4.8664],
  'bodegraven': [52.0814, 4.7478],
  'alphen': [52.1277, 4.6538],
  'boskoop': [52.0744, 4.6561],
  'waddinxveen': [52.0442, 4.6400],
  'zoeterwoude': [52.1167, 4.4833],
  'leiderdorp': [52.1578, 4.5194],
  'oegstgeest': [52.1747, 4.4658],
  'voorschoten': [52.1286, 4.4506],
  'berkel en rodenrijs': [51.9833, 4.5500],
  'delfgauw': [52.0097, 4.4639],
  'huizen': [52.2961, 5.2406],
  'bussum': [52.2742, 5.1633],
  'naarden': [52.2986, 5.1606],
  'muiden': [52.3333, 5.0667],
  'weesp': [52.3083, 5.0450],
  'diemen': [52.3361, 4.9614],
  'amstelveen': [52.3075, 4.8597],
  'aalsmeer': [52.2667, 4.7633],
  'uithoorn': [52.2333, 4.8333],
  'abcoude': [52.2800, 4.9753],
  'de ronde venen': [52.2167, 4.8667],
};

const NL_CENTER: [number, number] = [52.37, 5.29];
const REGIONS = Object.keys(PROVINCE_COORDS);


interface MapProfessional {
  user_id: string;
  first_name: string;
  last_name: string;
  photo_url?: string;
  availability_status: string;
  average_rating?: number | null;
  total_reviews: number;
  years_self_employed: number;
  city: string;
  active_regions: string[];
  professional_types: string[];
  hourly_rate?: number;
  // computed
  lat: number;
  lng: number;
  /** Ankerpunt zonder jitter (stads- of provinciecentroïde): basis voor de straaltelling. */
  ankerLat: number;
  ankerLng: number;
}

interface PoolSetup {
  config: {
    vitaliteits_niveau: 'A' | 'B' | 'C' | null;
    arbo_module_actief: boolean;
    aantal_medewerkers: number | null;
  } | null;
  selectie: { professional_type: string; bron: 'niveau' | 'arbo' | 'handmatig'; gewenste_experts: number }[];
  disciplines: { professional_type: string; label: string; category: string }[];
}

function getExperienceLabel(years: number): { label: string; color: string } {
  if (years <= 2) return { label: 'Starter (0–2 jr)', color: 'bg-blue-100 text-blue-700' };
  if (years <= 5) return { label: 'Gevorderd (3–5 jr)', color: 'bg-green-100 text-green-700' };
  if (years <= 10) return { label: 'Ervaren (6–10 jr)', color: 'bg-yellow-100 text-yellow-700' };
  return { label: 'Expert (11+ jr)', color: 'bg-purple-100 text-purple-700' };
}

function getAvailabilityDot(status: string): { color: string; title: string } {
  if (status === 'immediately') return { color: 'bg-green-500', title: 'Beschikbaar' };
  if (status === 'specific_date') return { color: 'bg-orange-400', title: 'Binnenkort beschikbaar' };
  return { color: 'bg-red-500', title: 'Niet beschikbaar' };
}

function makeMarkerIcon(status: string) {
  const color = status === 'immediately' ? '#22c55e' : status === 'specific_date' ? '#fb923c' : '#ef4444';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="36" viewBox="0 0 24 36">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24s12-15 12-24C24 5.373 18.627 0 12 0z" fill="${color}" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="5" fill="white" opacity="0.9"/>
  </svg>`;
  return L.divIcon({
    html: svg,
    className: '',
    iconSize: [24, 36],
    iconAnchor: [12, 36],
    popupAnchor: [0, -36],
  });
}

function addJitter(lat: number, lng: number, index: number): [number, number] {
  // Spread markers in same province using deterministic offset
  const angle = (index * 137.5 * Math.PI) / 180;
  const radius = 0.04 + (index % 5) * 0.025;
  return [lat + Math.sin(angle) * radius, lng + Math.cos(angle) * radius];
}

function ankerVoor(p: Omit<MapProfessional, 'lat' | 'lng' | 'ankerLat' | 'ankerLng'>): [number, number] {
  // 1. Try city lookup (woonadres leidend, geen straatnaam getoond)
  if (p.city) {
    const key = p.city.toLowerCase().trim();
    if (CITY_COORDS[key]) return CITY_COORDS[key];
    // Try matching start of city name (e.g. "Utrecht-Noord" → "utrecht")
    const match = Object.keys(CITY_COORDS).find(c => key.startsWith(c) || c.startsWith(key));
    if (match) return CITY_COORDS[match];
  }
  // 2. Fallback: province centroid from active_regions
  const region = p.active_regions?.[0];
  if (region && PROVINCE_COORDS[region]) return PROVINCE_COORDS[region];
  // 3. Last resort: center of NL
  return NL_CENTER;
}

/** Markerpositie = anker + jitter (alleen voor weergave, zodat markers niet stapelen). */
function positionProfessional(p: Omit<MapProfessional, 'lat' | 'lng' | 'ankerLat' | 'ankerLng'>, index: number) {
  const [ankerLat, ankerLng] = ankerVoor(p);
  const [lat, lng] = addJitter(ankerLat, ankerLng, index);
  return { lat, lng, ankerLat, ankerLng };
}

function MapFlyTo({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    map.flyTo([lat, lng], 9, { duration: 0.8 });
  }, [map, lat, lng]);
  return null;
}

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

const RADIUS_MIN = 5;
const RADIUS_MAX = 150;
const RADIUS_DEFAULT = 25;

function makePinIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="44" viewBox="0 0 24 36">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24s12-15 12-24C24 5.373 18.627 0 12 0z" fill="#2563eb" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="12" r="5" fill="white"/>
    <circle cx="12" cy="12" r="2" fill="#2563eb"/>
  </svg>`;
  return L.divIcon({ html: svg, className: '', iconSize: [32, 44], iconAnchor: [16, 44] });
}

/** Klik op de kaart plaatst / verplaatst de zoekpin */
function PinPlacer({ enabled, onPlace }: { enabled: boolean; onPlace: (lat: number, lng: number) => void }) {
  const map = useMapEvents({
    click: e => {
      if (enabled) onPlace(e.latlng.lat, e.latlng.lng);
    },
  });
  useEffect(() => {
    const el = map.getContainer();
    el.style.cursor = enabled ? 'crosshair' : '';
    return () => { el.style.cursor = ''; };
  }, [enabled, map]);
  return null;
}

/** Zoomt de kaart zodat de hele cirkel in beeld is */
function FitCircle({ lat, lng, radiusKm }: { lat: number; lng: number; radiusKm: number }) {
  const map = useMap();
  useEffect(() => {
    const bounds = L.latLng(lat, lng).toBounds(radiusKm * 2000);
    map.fitBounds(bounds, { padding: [30, 30], animate: true });
  }, [lat, lng, radiusKm, map]);
  return null;
}

export const EmployerMapSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [professionals, setProfessionals] = useState<MapProfessional[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [flyTo, setFlyTo] = useState<{ lat: number; lng: number } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Regio-pin met actieradius
  const [pin, setPin] = useState<{ lat: number; lng: number } | null>(null);
  const [radiusKm, setRadiusKm] = useState(RADIUS_DEFAULT);
  const [placingPin, setPlacingPin] = useState(false);
  const pinIcon = useRef(makePinIcon()).current;

  // Pool-instellingen (van /employer/pool-setup) toepassen als disciplinefilter
  const [pool, setPool] = useState<PoolSetup | null>(null);
  const [poolFilter, setPoolFilter] = useState(false);
  const [showDekking, setShowDekking] = useState(true);

  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    professional_type: searchParams.get('professional_type') ?? '',
    name: '',
    province: '',
  });

  const fetchProfessionals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.professional_type) params.set('professional_type', filters.professional_type);
      if (filters.name) {
        const parts = filters.name.trim().split(' ');
        if (parts[0]) params.set('first_name', parts[0]);
        if (parts[1]) params.set('last_name', parts.slice(1).join(' '));
      }
      if (filters.province) params.set('province', filters.province);

      const res = await api.get(`/employer/professionals-map?${params.toString()}`);
      if (res.data.success) {
        const raw: Omit<MapProfessional, 'lat' | 'lng' | 'ankerLat' | 'ankerLng'>[] = res.data.data;
        const positioned = raw.map((p, i) => ({ ...p, ...positionProfessional(p, i) }));
        setProfessionals(positioned);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfessionals();
    // Pool-setup is optioneel (andere permissie); bij 403/404 gewoon geen pool-filter
    api.get('/employer/pool-setup')
      .then(res => {
        const data: PoolSetup | undefined = res.data?.data;
        if (data && data.selectie?.length) {
          setPool(data);
          // Via URL gevraagde rol die (nog) niet in de opgeslagen pool zit: pool-filter uit laten
          const gevraagd = searchParams.get('professional_type');
          const inPool = !gevraagd || data.selectie.some(s => s.professional_type === gevraagd);
          setPoolFilter(inPool);
        }
      })
      .catch(() => { /* geen pool-toegang of niet ingericht */ });
    // Bewust alleen bij mount: eerste lading + beginwaarde uit de URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const poolTypes = React.useMemo(
    () => new Set(pool?.selectie.map(s => s.professional_type) ?? []),
    [pool]
  );
  const poolActive = poolFilter && poolTypes.size > 0;

  // Rol-dropdown beperken tot pool-disciplines zolang pool-filter aanstaat
  useEffect(() => {
    if (!poolActive) return;
    setFilters(f => (f.professional_type && !poolTypes.has(f.professional_type) ? { ...f, professional_type: '' } : f));
  }, [poolActive, poolTypes]);

  const inPool = React.useMemo(() => {
    if (!poolActive) return professionals;
    return professionals.filter(p => p.professional_types?.some(t => poolTypes.has(t)));
  }, [professionals, poolActive, poolTypes]);

  const inRadius = React.useMemo(() => {
    if (!pin) return inPool;
    // Op het anker, niet op de gejitterde markerpositie (die wijkt tot ~15 km af).
    return inPool.filter(p => haversineKm(pin.lat, pin.lng, p.ankerLat, p.ankerLng) <= radiusKm);
  }, [inPool, pin, radiusKm]);

  // Dekking per pool-discipline: aantal professionals in scope vs. gewenst aantal
  const dekking = React.useMemo(() => {
    if (!pool || !poolActive) return [];
    const labelMap = new Map(pool.disciplines.map(d => [d.professional_type, d.label]));
    return pool.selectie
      .map(sel => {
        const aanwezig = inRadius.filter(p => p.professional_types?.includes(sel.professional_type)).length;
        return {
          type: sel.professional_type,
          label: labelMap.get(sel.professional_type)
            || PROFESSIONAL_TYPE_LABELS[sel.professional_type as keyof typeof PROFESSIONAL_TYPE_LABELS]
            || sel.professional_type,
          bron: sel.bron,
          gewenst: sel.gewenste_experts,
          aanwezig,
        };
      })
      .sort((a, b) => (a.aanwezig - a.gewenst) - (b.aanwezig - b.gewenst) || a.label.localeCompare(b.label));
  }, [pool, poolActive, inRadius]);
  const tekortCount = dekking.filter(d => d.aanwezig < d.gewenst).length;

  const poolSummary = pool
    ? [
        pool.config?.vitaliteits_niveau ? `Niveau ${pool.config.vitaliteits_niveau}` : null,
        pool.config?.arbo_module_actief ? 'Arbo' : null,
        `${poolTypes.size} discipline${poolTypes.size !== 1 ? 's' : ''}`,
      ].filter(Boolean).join(' · ')
    : '';

  const placePin = (lat: number, lng: number) => {
    setPin({ lat, lng });
    setPlacingPin(false);
    setFlyTo(null);
  };

  const clearPin = () => {
    setPin(null);
    setPlacingPin(false);
  };

  const handleSelectFromMap = (id: string) => {
    setSelectedId(id);
    // scroll list to the selected card
    setTimeout(() => {
      const el = document.getElementById(`pro-card-${id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleSelectFromList = (p: MapProfessional) => {
    setSelectedId(p.user_id);
    setFlyTo({ lat: p.lat, lng: p.lng });
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Filter bar */}
      <div className="bg-white border-b px-4 py-3 flex flex-wrap gap-3 items-end z-10 shadow-sm">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Rol</label>
          <select
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
            value={filters.professional_type}
            onChange={e => setFilters(f => ({ ...f, professional_type: e.target.value }))}
          >
            <option value="">{poolActive ? 'Alle pool-disciplines' : 'Alle rollen'}</option>
            {(Object.entries(PROFESSIONAL_CATEGORIES) as [ProfessionalCategoryId, typeof PROFESSIONAL_CATEGORIES[ProfessionalCategoryId]][]).map(([catId, cat]) => (
              <optgroup key={catId} label={cat.label}>
                {cat.types
                  .filter(type => !poolActive || poolTypes.has(type))
                  .map(type => (
                    <option key={type} value={type}>{PROFESSIONAL_TYPE_LABELS[type]}</option>
                  ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Naam</label>
          <input
            type="text"
            placeholder="Zoek op naam..."
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
            value={filters.name}
            onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && fetchProfessionals()}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Provincie</label>
          <select
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filters.province}
            onChange={e => setFilters(f => ({ ...f, province: e.target.value }))}
          >
            <option value="">Alle provincies</option>
            {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <button
          onClick={fetchProfessionals}
          disabled={loading}
          className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Laden...' : 'Zoeken'}
        </button>
        <div className="ml-auto text-sm text-gray-500 self-center">
          {pin ? (
            <>
              <span className="font-semibold text-blue-700">{inRadius.length}</span> van {inPool.length} binnen {radiusKm} km
            </>
          ) : (
            <>{inPool.length} professional{inPool.length !== 1 ? 's' : ''} gevonden</>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white border-b px-4 py-1.5 flex gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" /> Beschikbaar</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-400 inline-block" /> Binnenkort</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" /> Niet beschikbaar</span>
      </div>

      {/* Pool-instellingen toepassen */}
      <div className="bg-white border-b px-4 py-2 flex flex-wrap items-center gap-3 text-sm">
        {pool ? (
          <>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="accent-blue-600 w-4 h-4"
                checked={poolFilter}
                onChange={e => setPoolFilter(e.target.checked)}
              />
              <span className="font-medium text-gray-800">Pool-instellingen toepassen</span>
            </label>
            <span className="text-xs text-gray-500">{poolSummary}</span>
            {poolActive && tekortCount > 0 && pin && (
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                {tekortCount} discipline{tekortCount !== 1 ? 's' : ''} onder gewenst aantal
              </span>
            )}
            <button
              onClick={() => navigate('/employer/pool-setup')}
              className="ml-auto text-xs text-blue-600 hover:text-blue-700 whitespace-nowrap"
            >
              Pool-instellingen aanpassen →
            </button>
          </>
        ) : (
          <>
            <span className="text-xs text-gray-500">
              Nog geen pool ingericht. Richt uw pool in om de kaart automatisch op uw disciplines te filteren.
            </span>
            <button
              onClick={() => navigate('/employer/pool-setup')}
              className="ml-auto text-xs text-blue-600 hover:text-blue-700 whitespace-nowrap"
            >
              Pool inrichten →
            </button>
          </>
        )}
      </div>

      {/* Regio-pin + actieradius */}
      <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 flex flex-wrap items-center gap-3 text-sm">
        {!pin ? (
          <>
            <button
              onClick={() => setPlacingPin(v => !v)}
              className={`px-3 py-1 rounded-lg border text-sm transition-colors ${
                placingPin
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-100'
              }`}
            >
              📍 {placingPin ? 'Klik op de kaart…' : 'Pin plaatsen'}
            </button>
            <span className="text-xs text-gray-600">
              {placingPin
                ? 'Klik op de kaart om het middelpunt van uw regio te kiezen.'
                : 'Plaats een pin en stel de actieradius in om te zien hoeveel professionals er in uw regio beschikbaar zijn.'}
            </span>
          </>
        ) : (
          <>
            <span className="text-blue-700 font-medium">📍 Regio</span>
            <label className="flex items-center gap-2">
              <span className="text-xs text-gray-600">Actieradius</span>
              <input
                type="range"
                min={RADIUS_MIN}
                max={RADIUS_MAX}
                step={5}
                value={radiusKm}
                onChange={e => setRadiusKm(Number(e.target.value))}
                className="w-40 accent-blue-600"
              />
              <input
                type="number"
                min={RADIUS_MIN}
                max={RADIUS_MAX}
                value={radiusKm}
                onChange={e => {
                  const v = Number(e.target.value);
                  if (!Number.isNaN(v)) setRadiusKm(Math.min(RADIUS_MAX, Math.max(RADIUS_MIN, v)));
                }}
                className="w-16 border border-gray-300 rounded px-2 py-0.5 text-sm text-right"
              />
              <span className="text-xs text-gray-600">km</span>
            </label>
            <span className="px-2 py-0.5 rounded-full bg-blue-600 text-white text-xs font-semibold">
              {inRadius.length} professional{inRadius.length !== 1 ? 's' : ''} in regio
            </span>
            <span className="text-xs text-gray-500">Sleep de pin om te verplaatsen</span>
            <button
              onClick={clearPin}
              className="ml-auto text-xs text-gray-600 hover:text-red-600 underline"
            >
              Pin verwijderen
            </button>
          </>
        )}
      </div>

      {/* Split view: map + list */}
      <div className="flex flex-1 overflow-hidden">
        {/* Map */}
        <div className="flex-1 relative">
          <MapContainer
            center={NL_CENTER}
            zoom={7}
            style={{ height: '100%', width: '100%' }}
            zoomControl={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {flyTo && <MapFlyTo lat={flyTo.lat} lng={flyTo.lng} />}
            <PinPlacer enabled={placingPin} onPlace={placePin} />
            {pin && (
              <>
                <FitCircle lat={pin.lat} lng={pin.lng} radiusKm={radiusKm} />
                <Circle
                  center={[pin.lat, pin.lng]}
                  radius={radiusKm * 1000}
                  pathOptions={{ color: '#2563eb', weight: 2, fillColor: '#3b82f6', fillOpacity: 0.12 }}
                />
                <Marker
                  position={[pin.lat, pin.lng]}
                  icon={pinIcon}
                  draggable
                  zIndexOffset={1000}
                  eventHandlers={{
                    dragend: e => {
                      const ll = (e.target as L.Marker).getLatLng();
                      setPin({ lat: ll.lat, lng: ll.lng });
                    },
                  }}
                />
              </>
            )}
            {inRadius.map(p => (
              <Marker
                key={p.user_id}
                position={[p.lat, p.lng]}
                icon={makeMarkerIcon(p.availability_status)}
                eventHandlers={{ click: () => handleSelectFromMap(p.user_id) }}
              >
                <Popup>
                  <div className="min-w-[180px]">
                    <div className="flex items-center gap-2 mb-2">
                      <BelagomProfilePhoto
                        src={p.photo_url}
                        size={36}
                      />
                      <div>
                        <div className="font-semibold text-sm">{p.first_name} {p.last_name}</div>
                        <div className="text-xs text-gray-500">{p.city}</div>
                      </div>
                    </div>
                    {p.professional_types.length > 0 && (
                      <div className="text-xs text-blue-600 mb-1">
                        {PROFESSIONAL_TYPE_LABELS[p.professional_types[0] as keyof typeof PROFESSIONAL_TYPE_LABELS] || p.professional_types[0]}
                      </div>
                    )}
                    <div className="flex items-center gap-1 mb-2">
                      <span className={`w-2 h-2 rounded-full ${getAvailabilityDot(p.availability_status).color}`} />
                      <span className="text-xs text-gray-600">{getAvailabilityDot(p.availability_status).title}</span>
                    </div>
                    {p.average_rating != null && (
                      <div className="text-xs text-gray-600 mb-2">
                        ★ {p.average_rating.toFixed(1)} ({p.total_reviews} reviews)
                      </div>
                    )}
                    <button
                      onClick={() => navigate(`/employer/professional/${p.user_id}`)}
                      className="w-full text-xs bg-blue-600 text-white rounded px-2 py-1 hover:bg-blue-700"
                    >
                      Bekijk profiel
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* List */}
        <div ref={listRef} className="w-80 xl:w-96 overflow-y-auto bg-white border-l flex-shrink-0">
          {poolActive && dekking.length > 0 && (
            <div className="border-b bg-gray-50">
              <button
                onClick={() => setShowDekking(v => !v)}
                className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100"
              >
                <span>
                  Pool-dekking {pin ? `binnen ${radiusKm} km` : '(heel NL)'}
                  {tekortCount > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                      {tekortCount} tekort
                    </span>
                  )}
                </span>
                <span className="text-gray-400">{showDekking ? '▾' : '▸'}</span>
              </button>
              {showDekking && (
                <div className="px-4 pb-2">
                  <table className="w-full text-xs">
                    <tbody>
                      {dekking.map(d => {
                        const ok = d.aanwezig >= d.gewenst;
                        return (
                          <tr
                            key={d.type}
                            className="cursor-pointer hover:bg-white"
                            onClick={() => setFilters(f => ({ ...f, professional_type: f.professional_type === d.type ? '' : d.type }))}
                            title="Klik om op deze discipline te filteren"
                          >
                            <td className="py-0.5 pr-2">
                              <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${ok ? 'bg-green-500' : d.aanwezig > 0 ? 'bg-amber-400' : 'bg-red-500'}`} />
                              <span className={filters.professional_type === d.type ? 'font-semibold text-blue-700' : 'text-gray-700'}>
                                {d.label}
                              </span>
                            </td>
                            <td className={`py-0.5 text-right whitespace-nowrap font-medium ${ok ? 'text-green-700' : 'text-amber-700'}`}>
                              {d.aanwezig} / {d.gewenst}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="mt-1.5 flex items-center justify-between text-[11px] text-gray-500">
                    <span>aanwezig / gewenst per discipline</span>
                    <button
                      onClick={() => navigate('/employer/pool-setup')}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      Aanpassen →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {inRadius.length === 0 && !loading && (
            <div className="p-6 text-center text-gray-400 text-sm">
              {pin && inPool.length > 0
                ? `Geen professionals binnen ${radiusKm} km. Vergroot de actieradius of verplaats de pin.`
                : poolActive && professionals.length > 0
                  ? 'Geen professionals binnen uw pool-disciplines. Zet het pool-filter uit of pas de pool-instellingen aan.'
                  : 'Geen professionals gevonden. Pas de filters aan.'}
            </div>
          )}
          {inRadius.map(p => {
            const dot = getAvailabilityDot(p.availability_status);
            const exp = getExperienceLabel(p.years_self_employed);
            const isSelected = p.user_id === selectedId;
            const typeLabel = PROFESSIONAL_TYPE_LABELS[p.professional_types[0] as keyof typeof PROFESSIONAL_TYPE_LABELS] || p.professional_types[0];

            return (
              <div
                key={p.user_id}
                id={`pro-card-${p.user_id}`}
                onClick={() => handleSelectFromList(p)}
                className={`flex items-start gap-3 px-4 py-3 border-b cursor-pointer transition-colors ${
                  isSelected ? 'bg-blue-50 border-l-2 border-l-blue-500' : 'hover:bg-gray-50'
                }`}
              >
                <div className="relative flex-shrink-0">
                  <BelagomProfilePhoto
                    src={p.photo_url}
                    size={48}
                  />
                  <span
                    className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${dot.color}`}
                    title={dot.title}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-1">
                    <div>
                      <div className="font-semibold text-sm text-gray-900 truncate">
                        {p.first_name} {p.last_name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{p.city}</div>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); navigate(`/employer/professional/${p.user_id}`); }}
                      className="text-xs text-blue-600 hover:text-blue-700 whitespace-nowrap flex-shrink-0"
                    >
                      Profiel →
                    </button>
                  </div>
                  {typeLabel && (
                    <div className="text-xs text-blue-600 mt-0.5 truncate">{typeLabel}</div>
                  )}
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${exp.color}`}>
                      {exp.label}
                    </span>
                    {p.average_rating != null && p.total_reviews > 0 && (
                      <span className="text-xs text-gray-500">
                        ★ {p.average_rating.toFixed(1)} ({p.total_reviews})
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
